import React, { FC, useContext, useEffect, useMemo, useState } from 'react';
import { v4 as uuid } from 'uuid';
import { Button, Form, Icon, TextArea } from 'semantic-ui-react';
import { useCentralCalls } from '@src/rest/CentralCalls';
import { ProjectContext } from '@src/views/pages/projects/ProjectIndexPage';
import { LoadingIndicator } from '@src/views/components/util/loading/LoadingIndicator';
import { EmptyCollectionMessage } from '@src/views/components/util/messaging/EmptyCollectionMessage';
import { SelectField } from '@src/views/components/forms';
import { formatOption } from '@src/util';
// RunInsert/RunUpdate target the EF DbSet name (plural), not raw SQL table name.
const PROJECT_SURVEY_RESPONSE_TABLE = 'ProjectSurveyResponses';
type QuestionType = 'TEXT' | 'NUMBER' | 'DROPDOWN' | 'MULTISELECT' | string;
interface SurveyQuestionOption {
    surveyQuestionOptionId: string;
    optionText: string;
}
interface SurveyQuestionItem {
    surveyQuestionId: string;
    question: string;
    questionType: QuestionType;
    required: boolean;
    description?: string | null;
    options: SurveyQuestionOption[];
    existingResponseId?: string;
    existingResponseText?: string;
    existingResponseOptionId?: string;
}
interface QuestionAnswer {
    text: string;
    optionId: string;
}
type WorkflowTab = 'survey' | 'review';
const parseArrayPayload = ( response: any ): any[] => {
    if( Array.isArray( response ) ) return response;
    const candidates = [ response?.result, response?.Result, response?.data, response?.Data ];
    for( const candidate of candidates ){
        if( Array.isArray( candidate ) ) return candidate;
        if( typeof candidate === 'string' ){
            try {
                const parsed = JSON.parse( candidate );
                if( Array.isArray( parsed ) ) return parsed;
            } catch {
                // ignore invalid JSON wrappers
            }
        }
    }
    if( typeof response === 'string' ){
        try {
            const parsed = JSON.parse( response );
            if( Array.isArray( parsed ) ) return parsed;
        } catch {
            // ignore invalid raw payloads
        }
    }
    return [];
};
const parseSurveyQuestionsResponse = ( response: any ): SurveyQuestionItem[] => {
    const topRows = parseArrayPayload( response );
    if( topRows.length === 0 ) return [];
    const first = topRows[ 0 ];
    const rawJson = typeof first?.JSON === 'string' ? first.JSON : null;
    const questionRows = rawJson ? parseArrayPayload( rawJson ) : topRows;
    return questionRows.map( ( row: any ) => {
        const required = typeof row?.Required === 'boolean'
            ? row.Required
            : typeof row?.required === 'boolean'
                ? row.required
                : Number( row?.Required ?? row?.required ?? 0 ) === 1;
        const rawOptions = Array.isArray( row?.Options )
            ? row.Options
            : typeof row?.Options === 'string'
                ? parseArrayPayload( row.Options )
                : [];
        return {
            surveyQuestionId: `${ row?.SurveyQuestionId || row?.surveyQuestionId || '' }`,
            question: `${ row?.Question || row?.question || '' }`,
            questionType: `${ row?.QuestionType || row?.questionType || 'TEXT' }`,
            required,
            description: row?.Description ?? row?.description ?? null,
            existingResponseId: `${ row?.ProjectSurveyResponseId || row?.projectSurveyResponseId || row?.ClientSurveyReponseId || row?.clientSurveyReponseId || row?.ResponseId || row?.responseId || '' }` || undefined,
            existingResponseText: `${ row?.SurveyQuestionResponse || row?.surveyQuestionResponse || '' }`,
            existingResponseOptionId: `${ row?.SurveyQuestionOptionId || row?.surveyQuestionOptionId || '' }`,
            options: rawOptions.map( ( option: any ) => ( {
                surveyQuestionOptionId: `${ option?.SurveyQuestionOptionId || option?.surveyQuestionOptionId || '' }`,
                optionText: `${ option?.OptionText || option?.optionText || '' }`,
            } ) ).filter( option => option.surveyQuestionOptionId ),
        };
    } ).filter( question => question.surveyQuestionId );
};
const questionUsesOption = ( questionType: QuestionType ): boolean => {
    const normalized = questionType.toUpperCase();
    return normalized === 'DROPDOWN' || normalized === 'MULTISELECT';
};
// Brand palette. Purple tokens come from the Corval theme (CorvalUIColor.PURPLE ->
// --color-purple), so this stays in sync with the rest of the product's branding.
const BRAND = {
    purple:     'var(--color-purple)',
    purpleSoft: 'var(--color-purpleBackground)',
    border:     '#ebebeb',
    borderSoft: '#f0f0f0',
    surface:    '#ffffff',
    hover:      '#fafafa',
    ink:        '#2b2b2b',
    textMuted:  '#888888',
    textSubtle: '#aaaaaa',
};
const workflowCardStyle: React.CSSProperties = {
    background:    BRAND.surface,
    border:        `1px solid ${ BRAND.border }`,
    borderRadius:  10,
    boxShadow:     '0 1px 3px rgba(16, 16, 16, 0.05)',
};
const typeBadgeStyle: React.CSSProperties = {
    fontSize:      '0.68rem',
    fontWeight:    700,
    letterSpacing: 0.4,
    color:         '#8a8a8a',
    background:    '#f4f4f4',
    border:        '1px solid #ececec',
    borderRadius:  4,
    padding:       '2px 7px',
};
const requiredBadgeStyle: React.CSSProperties = {
    fontSize:      '0.68rem',
    fontWeight:    700,
    letterSpacing: 0.4,
    color:         BRAND.purple,
    background:    BRAND.purpleSoft,
    borderRadius:  4,
    padding:       '2px 7px',
};
const getTypeBadgeLabel = ( questionType: QuestionType ): string => {
    switch( questionType.toUpperCase() ){
        case 'DROPDOWN':
        case 'MULTISELECT':
            return 'SELECTION';
        case 'NUMBER':
            return 'NUMBER';
        default:
            return 'FREE TEXT';
    }
};
interface DiscoveryWorkflowProps {
    projectId?: string | null;
    clientId?: string | null;
}
const DiscoveryWorkflow: FC<DiscoveryWorkflowProps> = ( {
    projectId: projectIdProp,
    clientId: clientIdProp,
} ) => {
    const { projectId: contextProjectId } = useContext( ProjectContext );
    const projectId = projectIdProp ?? contextProjectId;
    const clientId = clientIdProp ?? null;
    const isClientMode = !!clientId;
    const {
        executeStoredProcedure,
        RunInsert,
        RunUpdate,
    } = useCentralCalls();
    const [ questions, setQuestions ]                       = useState<SurveyQuestionItem[]>( [] ),
          [ currentIndex, setCurrentIndex ]                 = useState(0),
          [ answers, setAnswers ]                           = useState<Record<string, QuestionAnswer>>( {} ),
          [ responseIdByQuestion, setResponseIdByQuestion ] = useState<Record<string, string>>( {} ),
          [ isLoading, setIsLoading ]                       = useState( true ),
          [ isSaving, setIsSaving ]                         = useState( false ),
          [ error, setError ]                               = useState( '' ),
          [ status, setStatus ]                             = useState( '' ),
          [ activeTab, setActiveTab ]                       = useState<WorkflowTab>('survey');

    const loadData = async () => {
        if( !projectId && !clientId ){
            setError( 'No project or client is selected.' );
            setIsLoading( false );
            return;
        }
        setIsLoading( true );
        setError( '' );
        try {
            const questionResponse = await executeStoredProcedure( 'sp_get_SurveyQuestions', {
                projectId: projectId || null,
                clientId: clientId || null,
            } );
            const loadedQuestions = parseSurveyQuestionsResponse( questionResponse );
            const loadedAnswers: Record<string, QuestionAnswer> = {};
            const responseIds: Record<string, string> = {};
            loadedQuestions.forEach( question => {
                if( question.existingResponseId ){
                    responseIds[ question.surveyQuestionId ] = question.existingResponseId;
                }
                if( question.existingResponseId || question.existingResponseText || question.existingResponseOptionId ){
                    loadedAnswers[ question.surveyQuestionId ] = {
                        text: question.existingResponseText ?? '',
                        optionId: question.existingResponseOptionId ?? '',
                    };
                }
            } );
            setQuestions( loadedQuestions );
            setAnswers( loadedAnswers );
            setResponseIdByQuestion( responseIds );
            setCurrentIndex( 0 );
        } catch( err ){
            console.error( '[DiscoveryWorkflow] failed to load survey data:', err );
            setError( 'Could not load survey questions.' );
        } finally {
            setIsLoading( false );
        }
    };
    useEffect( () => {
        loadData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ projectId, clientId ] );
    const currentQuestion = questions[ currentIndex ];
    const currentQuestionId = currentQuestion?.surveyQuestionId ?? '';
    const currentAnswer = answers[ currentQuestionId ] ?? { text: '', optionId: '' };
    const totalQuestions = questions.length;
    const progressPercent = totalQuestions > 0
        ? Math.round( ( ( currentIndex + 1 ) / totalQuestions ) * 100 )
        : 0;
    const optionChoices = useMemo( () => {
        if( !currentQuestion ) return [];
        return currentQuestion.options.map( option => formatOption( option.surveyQuestionOptionId, option.optionText ) );
    }, [ currentQuestion ] );
    const getReviewResponseText = ( question: SurveyQuestionItem ): string => {
        const answer = answers[ question.surveyQuestionId ];
        if( !answer ) return 'No response yet.';
        if( questionUsesOption( question.questionType ) ){
            const selected = question.options.find( option => option.surveyQuestionOptionId === answer.optionId );
            return selected?.optionText || 'No response yet.';
        }
        const value = answer.text.trim();
        return value || 'No response yet.';
    };
    const setCurrentAnswer = ( next: Partial<QuestionAnswer> ) => {
        if( !currentQuestionId ) return;
        setAnswers( prev => ( {
            ...prev,
            [ currentQuestionId ]: {
                text: next.text ?? ( prev[ currentQuestionId ]?.text ?? '' ),
                optionId: next.optionId ?? ( prev[ currentQuestionId ]?.optionId ?? '' ),
            },
        } ) );
    };
    const validateCurrentAnswer = (): string | null => {
        if( !currentQuestion ) return 'No question is selected.';
        const isRequired = !!currentQuestion.required;
        if( questionUsesOption( currentQuestion.questionType ) ){
            if( isRequired && !currentAnswer.optionId ) return 'This question is required. Please select an option.';
            return null;
        }
        const value = currentAnswer.text.trim();
        if( isRequired && !value ) return 'This question is required. Please enter a response.';
        if( value.length > 500 ) return 'Response cannot exceed 500 characters.';
        return null;
    };
    const saveCurrentResponse = async (): Promise<boolean> => {
        if( ( !projectId && !clientId ) || !currentQuestion ) return false;
        const validationError = validateCurrentAnswer();
        if( validationError ){
            setError( validationError );
            return false;
        }
        setIsSaving( true );
        setError( '' );
        setStatus( '' );
        try {
            const useOption = questionUsesOption( currentQuestion.questionType );
            const existingId = responseIdByQuestion[ currentQuestion.surveyQuestionId ];
            const hasValue = useOption
                ? !!currentAnswer.optionId
                : !!currentAnswer.text.trim();
            if( !currentQuestion.required && !hasValue ){
                setStatus( 'Optional question skipped.' );
                return true;
            }
            const payload = {
                ProjectId: isClientMode ? null : projectId,
                ClientId: isClientMode ? clientId : null,
                SurveyQuestionId: currentQuestion.surveyQuestionId,
                SurveyQuestionResponse: useOption ? null : currentAnswer.text.trim(),
                SurveyQuestionOptionId: useOption ? currentAnswer.optionId : null,
            };
            if( existingId ){
                await RunUpdate( PROJECT_SURVEY_RESPONSE_TABLE, existingId, payload );
            } else {
                const newId = uuid();
                await RunInsert( PROJECT_SURVEY_RESPONSE_TABLE, {
                    Id: newId,
                    ...payload,
                } );
                setResponseIdByQuestion( prev => ( {
                    ...prev,
                    [ currentQuestion.surveyQuestionId ]: newId,
                } ) );
            }
            setStatus( 'Response saved.' );
            return true;
        } catch( err ){
            console.error( '[DiscoveryWorkflow] failed to save response:', err );
            const message = err instanceof Error ? err.message : 'Could not save response.';
            setError( message );
            return false;
        } finally {
            setIsSaving( false );
        }
    };
    const handlePrevious = () => {
        if( currentIndex === 0 ) return;
        setError( '' );
        setStatus( '' );
        setCurrentIndex( prev => prev - 1 );
    };
    const handleNext = async () => {
        const saved = await saveCurrentResponse();
        if( !saved ) return;
        if( currentIndex < totalQuestions - 1 ){
            setCurrentIndex( prev => prev + 1 );
            setStatus( '' );
        } else {
            setActiveTab( 'review' );
            setStatus( '' );
        }
    };
    if( isLoading ){
        return <LoadingIndicator text={ 'Loading survey workflow...' }/>;
    }
    if( !projectId && !clientId ){
        return (
            <div style={ workflowCardStyle }>
                <div style={ { padding: '44px 24px', textAlign: 'center' } }>
                    <Icon name={ 'folder open outline' } size={ 'big' } style={ { color: BRAND.textSubtle } }/>
                    <div style={ { fontWeight: 600, color: BRAND.ink, marginTop: 12 } }>
                        No { isClientMode ? 'client' : 'project' } selected
                    </div>
                    <div style={ { fontSize: '0.9rem', color: BRAND.textMuted, marginTop: 4 } }>
                        This workflow requires an active { isClientMode ? 'client' : 'project' }.
                    </div>
                </div>
            </div>
        );
    }
    if( questions.length === 0 ){
        return (
            <div style={ workflowCardStyle }>
                <EmptyCollectionMessage
                    icon={ 'question circle outline' }
                    header={ 'No questions yet' }
                    content={ 'Add questions first, then return to run the discovery workflow.' }
                />
                <div style={ { textAlign: 'center', paddingBottom: 24 } }>
                    <Button basic color={ 'purple' } size={ 'small' } onClick={ loadData }>
                        <Icon name={ 'refresh' }/> Reload questions
                    </Button>
                </div>
            </div>
        );
    }
    const answeredCount = questions.filter( question => {
        const answer = answers[ question.surveyQuestionId ];
        if( !answer ) return false;
        return questionUsesOption( question.questionType ) ? !!answer.optionId : !!answer.text.trim();
    } ).length;
    const isLastQuestion = currentIndex === totalQuestions - 1;
    const workflowTabs: { key: WorkflowTab; label: string }[] = [
        { key: 'survey', label: 'Survey' },
        { key: 'review', label: 'Review' },
    ];
    return (
        <div style={ workflowCardStyle }>

            {/* ── Header ── */}
            <div style={ { padding: '18px 22px 0' } }>
                <div style={ { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 } }>
                    <div>
                        <div style={ { fontSize: '1.15rem', fontWeight: 700, color: BRAND.ink } }>
                            Discovery Workflow
                        </div>
                        <div style={ { fontSize: '0.85rem', color: BRAND.textMuted, marginTop: 2 } }>
                            Answer each question and move through the workflow with Back / Next.
                        </div>
                    </div>
                    <div style={ {
                        flexShrink:    0,
                        fontSize:      '0.75rem',
                        fontWeight:    600,
                        color:         BRAND.purple,
                        background:    BRAND.purpleSoft,
                        borderRadius:  999,
                        padding:       '4px 12px',
                        whiteSpace:    'nowrap',
                    } }>
                        { answeredCount } / { totalQuestions } answered
                    </div>
                </div>

                {/* ── Segmented tabs ── */}
                <div style={ {
                    display:      'inline-flex',
                    background:   BRAND.borderSoft,
                    borderRadius: 999,
                    padding:      3,
                    marginTop:    16,
                } }>
                    { workflowTabs.map( tab => {
                        const active = activeTab === tab.key;
                        return (
                            <button
                                key={ tab.key }
                                type={ 'button' }
                                onClick={ () => setActiveTab( tab.key ) }
                                style={ {
                                    border:       'none',
                                    cursor:       'pointer',
                                    fontSize:     '0.82rem',
                                    fontWeight:   600,
                                    padding:      '6px 20px',
                                    borderRadius: 999,
                                    color:        active ? BRAND.purple : BRAND.textMuted,
                                    background:   active ? BRAND.surface : 'transparent',
                                    boxShadow:    active ? '0 1px 2px rgba(16, 16, 16, 0.12)' : 'none',
                                    transition:   'all 0.15s ease',
                                } }>
                                { tab.label }
                            </button>
                        );
                    } ) }
                </div>
            </div>

            <div style={ { height: 1, background: BRAND.border, marginTop: 14 } }/>

            {/* ── Body ── */}
            <div style={ { padding: '20px 22px' } }>

                { activeTab === 'survey' && (
                    <>
                        {/* Progress */}
                        <div style={ { marginBottom: 22 } }>
                            <div style={ { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 } }>
                                <span style={ { fontSize: '0.72rem', fontWeight: 700, letterSpacing: 0.6, color: BRAND.purple } }>
                                    QUESTION { currentIndex + 1 } OF { totalQuestions }
                                </span>
                                <span style={ { fontSize: '0.78rem', color: BRAND.textSubtle } }>
                                    { progressPercent }%
                                </span>
                            </div>
                            <div style={ { height: 6, background: BRAND.borderSoft, borderRadius: 999, overflow: 'hidden' } }>
                                <div style={ {
                                    width:      `${ progressPercent }%`,
                                    height:     '100%',
                                    background: 'linear-gradient(90deg, var(--color-darkPurple), var(--color-purple))',
                                    borderRadius: 999,
                                    transition: 'width 0.35s ease',
                                } }/>
                            </div>
                        </div>

                        {/* Question card */}
                        <div style={ {
                            background:      BRAND.surface,
                            border:          `1px solid ${ BRAND.border }`,
                            borderRadius:    6,
                            padding:         '16px 18px',
                        } }>
                            <div style={ { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, flexWrap: 'wrap' } }>
                                <span style={ typeBadgeStyle }>{ getTypeBadgeLabel( currentQuestion.questionType ) }</span>
                                { currentQuestion.required && (
                                    <span style={ requiredBadgeStyle }>REQUIRED</span>
                                ) }
                            </div>

                            <div style={ { fontSize: '1.1rem', fontWeight: 600, color: BRAND.ink, lineHeight: 1.4 } }>
                                { currentQuestion.question }
                            </div>
                            { currentQuestion.description && (
                                <div style={ { color: BRAND.textMuted, fontSize: '0.9rem', marginTop: 6, lineHeight: 1.5 } }>
                                    { currentQuestion.description }
                                </div>
                            ) }

                            <div style={ { marginTop: 16 } }>
                                <Form>
                                    { questionUsesOption( currentQuestion.questionType ) ? (
                                        <SelectField
                                            fluid
                                            selection
                                            name={ 'surveyOption' }
                                            label={ 'Select an option' }
                                            placeholder={ 'Choose an option' }
                                            includeErrorMessage={ false }
                                            preventFirstSelection={ false }
                                            options={ optionChoices }
                                            value={ currentAnswer.optionId || undefined }
                                            onChange={ value => {
                                                setCurrentAnswer( { optionId: String( value ?? '' ) } );
                                                setError( '' );
                                                setStatus( '' );
                                            } }
                                            disabled={ isSaving }
                                        />
                                    ) : (
                                        <Form.Field>
                                            <label>Your response</label>
                                            <TextArea
                                                value={ currentAnswer.text }
                                                maxLength={ 500 }
                                                placeholder={ 'Type your response...' }
                                                onChange={ ( _, data ) => {
                                                    setCurrentAnswer( { text: String( data.value ?? '' ) } );
                                                    setError( '' );
                                                    setStatus( '' );
                                                } }
                                                disabled={ isSaving }
                                                rows={ 5 }
                                            />
                                            <div style={ { textAlign: 'right', color: BRAND.textSubtle, marginTop: 4, fontSize: '0.78rem' } }>
                                                { currentAnswer.text.length } / 500
                                            </div>
                                        </Form.Field>
                                    ) }
                                </Form>
                            </div>

                            { error && (
                                <div style={ {
                                    display:      'flex',
                                    alignItems:   'center',
                                    gap:          6,
                                    marginTop:    14,
                                    fontSize:     '0.85rem',
                                    color:        '#c0392b',
                                    background:   '#fdecea',
                                    border:       '1px solid #f5c6cb',
                                    borderRadius: 6,
                                    padding:      '8px 12px',
                                } }>
                                    <Icon name={ 'exclamation circle' } style={ { margin: 0 } }/>
                                    <span>{ error }</span>
                                </div>
                            ) }
                            { status && (
                                <div style={ {
                                    display:      'flex',
                                    alignItems:   'center',
                                    gap:          6,
                                    marginTop:    14,
                                    fontSize:     '0.85rem',
                                    color:        '#2e7d32',
                                    background:   '#edf7ee',
                                    border:       '1px solid #c8e6c9',
                                    borderRadius: 6,
                                    padding:      '8px 12px',
                                } }>
                                    <Icon name={ 'check circle' } style={ { margin: 0 } }/>
                                    <span>{ status }</span>
                                </div>
                            ) }
                        </div>

                        {/* Footer actions */}
                        <div style={ { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 18 } }>
                            <Button
                                basic
                                onClick={ handlePrevious }
                                disabled={ currentIndex === 0 || isSaving }>
                                <Icon name={ 'arrow left' }/>
                                Back
                            </Button>
                            <div style={ { display: 'flex', gap: 8 } }>
                                <Button
                                    basic
                                    color={ 'purple' }
                                    disabled={ isSaving }
                                    onClick={ saveCurrentResponse }>
                                    { isSaving ? 'Saving...' : 'Save Response' }
                                </Button>
                                <Button
                                    color={ 'purple' }
                                    disabled={ isSaving }
                                    onClick={ handleNext }>
                                    { isLastQuestion ? 'Finish' : 'Next' }
                                    <Icon name={ 'arrow right' } style={ { marginRight: 0, marginLeft: 6 } }/>
                                </Button>
                            </div>
                        </div>
                    </>
                ) }

                { activeTab === 'review' && (
                    <>
                        <div style={ { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 16 } }>
                            <div>
                                <div style={ { fontSize: '1rem', fontWeight: 700, color: BRAND.ink } }>Response Review</div>
                                <div style={ { fontSize: '0.85rem', color: BRAND.textMuted, marginTop: 2 } }>
                                    All questions and their current responses are shown below.
                                </div>
                            </div>
                            <div style={ {
                                flexShrink:   0,
                                fontSize:     '0.75rem',
                                fontWeight:   600,
                                color:        BRAND.purple,
                                background:   BRAND.purpleSoft,
                                borderRadius: 999,
                                padding:      '4px 12px',
                                whiteSpace:   'nowrap',
                            } }>
                                { answeredCount } / { totalQuestions } complete
                            </div>
                        </div>

                        { questions.map( ( question, index ) => {
                            const answer     = answers[ question.surveyQuestionId ];
                            const isAnswered = !!answer && ( questionUsesOption( question.questionType ) ? !!answer.optionId : !!answer.text.trim() );
                            const accent     = isAnswered ? BRAND.purple : '#dcdcdc';
                            return (
                                <div
                                    key={ question.surveyQuestionId }
                                    onClick={ () => { setActiveTab( 'survey' ); setCurrentIndex( index ); setError( '' ); setStatus( '' ); } }
                                    style={ {
                                        background:   BRAND.surface,
                                        border:       `1px solid ${ BRAND.border }`,
                                        borderLeft:   `3px solid ${ accent }`,
                                        borderRadius: 6,
                                        padding:      '14px 16px',
                                        marginBottom: 10,
                                        cursor:       'pointer',
                                        transition:   'background 0.12s',
                                    } }
                                    onMouseEnter={ e => ( e.currentTarget.style.background = BRAND.hover ) }
                                    onMouseLeave={ e => ( e.currentTarget.style.background = BRAND.surface ) }>

                                    <div style={ { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' } }>
                                        <span style={ { fontSize: '0.78rem', color: BRAND.textSubtle, fontWeight: 600 } }>
                                            Question { index + 1 }
                                        </span>
                                        { question.required && <span style={ requiredBadgeStyle }>REQUIRED</span> }
                                        <span style={ {
                                            marginLeft:  'auto',
                                            display:     'inline-flex',
                                            alignItems:  'center',
                                            gap:         4,
                                            fontSize:    '0.72rem',
                                            fontWeight:  700,
                                            letterSpacing: 0.3,
                                            color:       isAnswered ? '#2e7d32' : BRAND.textSubtle,
                                        } }>
                                            <Icon name={ isAnswered ? 'check circle' : 'circle outline' } style={ { margin: 0 } }/>
                                            { isAnswered ? 'ANSWERED' : 'UNANSWERED' }
                                        </span>
                                    </div>

                                    <div style={ { fontWeight: 600, color: BRAND.ink, marginBottom: 6, lineHeight: 1.4 } }>
                                        { question.question }
                                    </div>
                                    { question.description && (
                                        <div style={ { color: BRAND.textMuted, fontSize: '0.85rem', marginBottom: 8 } }>
                                            { question.description }
                                        </div>
                                    ) }
                                    <div style={ {
                                        background:   isAnswered ? '#fbfbfc' : '#fafafa',
                                        border:       `1px solid ${ BRAND.borderSoft }`,
                                        borderRadius: 4,
                                        padding:      '10px 12px',
                                        fontSize:     '0.9rem',
                                        color:        isAnswered ? BRAND.ink : BRAND.textSubtle,
                                        fontStyle:    isAnswered ? 'normal' : 'italic',
                                    } }>
                                        { getReviewResponseText( question ) }
                                    </div>
                                </div>
                            );
                        } ) }
                    </>
                ) }

            </div>
        </div>
    );
};
export default DiscoveryWorkflow;
